import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { IndianRupee, Users, FileText, TrendingUp, Download, FileSpreadsheet, Filter, RotateCcw, AlertTriangle, Lightbulb, Target, ArrowUpDown, ChevronLeft, ChevronRight, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell, PieChart, Pie, Legend } from "recharts";
import StatCard from "@/components/StatCard";
import CollapsibleSection from "@/components/CollapsibleSection";
import { fmtMoney } from "@/lib/format";

const EMPTY_FILTERS = { fy: "", date_from: "", date_to: "", department: "", category: "", supplier: "", material: "", location: "", po_status: "", method: "", auction: "" };
const RISK_STYLE = { HIGH: "border-[#FECACA] bg-[#FEE2E2] text-[#991B1B]", MEDIUM: "border-[#FDE68A] bg-[#FEF3C7] text-[#92400E]", LOW: "border-[#BBF7D0] bg-[#DCFCE7] text-[#166534]" };
const PIE_COLORS = ["#0F172A", "#2563EB", "#16A34A", "#D97706", "#DC2626", "#7C3AED", "#0891B2", "#DB2777", "#65A30D", "#64748B"];

function fyOptions() {
  const now = new Date();
  const y = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return [0, 1, 2, 3, 4].map((i) => `FY ${y - i}-${String(y - i + 1).slice(2)}`);
}

const Empty = ({ children }) => (
  <p className="px-5 py-8 text-center text-sm text-muted-foreground">{children}</p>
);

export default function SpendVisibility() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [granularity, setGranularity] = useState("monthly");
  const [data, setData] = useState(null);
  const [table, setTable] = useState({ rows: [], total: 0, page: 1, pages: 1 });
  const [tableQ, setTableQ] = useState({ search: "", sort: "date", order: "desc", page: 1 });
  const navigate = useNavigate();

  const load = useCallback(() => {
    const params = { ...applied, granularity };
    api.get("/analytics/spend", { params }).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [applied, granularity]);
  const loadTable = useCallback(() => {
    const params = { ...applied, ...tableQ, page_size: 25 };
    api.get("/analytics/spend/table", { params }).then((r) => setTable(r.data)).catch(() => {});
  }, [applied, tableQ]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadTable(); }, [loadTable]);

  const doExport = async (format) => {
    try {
      const r = await api.get("/analytics/spend/export", { params: { ...applied, format }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `spend-visibility.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Spend report downloaded (${format.toUpperCase()})`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data) return <div className="py-20 text-center text-sm text-muted-foreground" data-testid="spend-loading">Computing spend analytics…</div>;
  const k = data.kpis;
  const drill = (patch) => { const f = { ...applied, ...patch }; setFilters(f); setApplied(f); };

  const kpis = [
    { label: "Total Spend", value: fmtMoney(k.total_spend), icon: IndianRupee, tone: "blue", id: "spend-total" },
    { label: "Total Base Value", value: fmtMoney(k.total_base), icon: IndianRupee, tone: "slate", id: "spend-base" },
    { label: "Total GST", value: fmtMoney(k.total_gst), icon: IndianRupee, tone: "amber", id: "spend-gst" },
    { label: "Total Savings", value: fmtMoney(k.total_savings), icon: TrendingUp, tone: "green", id: "spend-savings" },
    { label: "Active Suppliers", value: k.active_suppliers, icon: Users, tone: "blue", id: "spend-suppliers" },
    { label: "Number of POs", value: k.po_count, icon: FileText, tone: "slate", id: "spend-pos" },
    { label: "Average PO Value", value: fmtMoney(k.avg_po_value), icon: IndianRupee, tone: "green", id: "spend-avg" },
    { label: "Spend at Risk", value: fmtMoney(data.at_risk.spend), icon: AlertTriangle, tone: "red", id: "spend-risk", sub: `${data.at_risk.po_count} POs` },
  ];

  const FilterSelect = ({ k2, label, options, testid }) => (
    <div>
      <Label className="text-xs">{label}</Label>
      <Select value={filters[k2] || "all"} onValueChange={(v) => setFilters({ ...filters, [k2]: v === "all" ? "" : v })}>
        <SelectTrigger data-testid={testid} className="mt-1 h-8 text-xs"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-white">
          <SelectItem value="all">All</SelectItem>
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div data-testid="spend-visibility-page" className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Spend Visibility &amp; Analytics</h1>
          <p className="text-sm text-muted-foreground">Understand where your procurement spend goes, who you buy from, what you spend on, and where savings opportunities exist.</p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button data-testid="spend-export-btn" className="bg-slate-900 hover:bg-slate-800"><Download className="mr-1.5 h-4 w-4" /> Export Report</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-white" align="end">
            <DropdownMenuItem data-testid="spend-export-pdf" onClick={() => doExport("pdf")}><Download className="mr-2 h-3.5 w-3.5" /> Download PDF</DropdownMenuItem>
            <DropdownMenuItem data-testid="spend-export-xlsx" onClick={() => doExport("xlsx")}><FileSpreadsheet className="mr-2 h-3.5 w-3.5" /> Export Excel (.xlsx)</DropdownMenuItem>
            <DropdownMenuItem data-testid="spend-export-csv" onClick={() => doExport("csv")}><FileText className="mr-2 h-3.5 w-3.5" /> Export CSV</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="rounded-lg border bg-card p-4" data-testid="spend-filter-bar">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-6">
          <div>
            <Label className="text-xs">Date Range</Label>
            <Select value={filters.fy || "current"} onValueChange={(v) => setFilters({ ...filters, fy: v === "current" ? "" : v, date_from: "", date_to: "" })}>
              <SelectTrigger data-testid="filter-fy" className="mt-1 h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="current">Current FY ({fyOptions()[0]})</SelectItem>
                {fyOptions().map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {filters.fy === "custom" && (
            <>
              <div><Label className="text-xs">From</Label><Input data-testid="filter-date-from" type="date" className="mt-1 h-8 text-xs" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} /></div>
              <div><Label className="text-xs">To</Label><Input data-testid="filter-date-to" type="date" className="mt-1 h-8 text-xs" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} /></div>
            </>
          )}
          <FilterSelect k2="department" label="Department" options={data.filters_meta.departments} testid="filter-department" />
          <FilterSelect k2="category" label="Category" options={data.filters_meta.categories} testid="filter-category" />
          <FilterSelect k2="supplier" label="Supplier" options={data.filters_meta.suppliers} testid="filter-supplier" />
          <FilterSelect k2="location" label="Location" options={data.filters_meta.locations} testid="filter-location" />
          <FilterSelect k2="po_status" label="PO Status" options={data.filters_meta.statuses} testid="filter-status" />
          <FilterSelect k2="method" label="Procurement Method" options={["RFQ", "Auction", "Direct Purchase", "Repeat Purchase"]} testid="filter-method" />
          <div>
            <Label className="text-xs">Material</Label>
            <Input data-testid="filter-material" className="mt-1 h-8 text-xs" placeholder="Material code…" value={filters.material} onChange={(e) => setFilters({ ...filters, material: e.target.value })} />
          </div>
          <FilterSelect k2="auction" label="Auction / Non-Auction" options={["auction", "non_auction"]} testid="filter-auction" />
        </div>
        <div className="mt-3 flex gap-2">
          <Button size="sm" data-testid="apply-filters-btn" onClick={() => setApplied({ ...filters })} className="bg-slate-900 hover:bg-slate-800"><Filter className="mr-1.5 h-3.5 w-3.5" /> Apply Filters</Button>
          <Button size="sm" variant="outline" data-testid="reset-filters-btn" onClick={() => { setFilters(EMPTY_FILTERS); setApplied(EMPTY_FILTERS); }}><RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Reset</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="spend-kpi-grid">
        {kpis.map((kk, i) => (
          <div key={kk.id} className="animate-fade-up" style={{ animationDelay: `${i * 40}ms` }}>
            <StatCard label={kk.label} value={kk.value} icon={kk.icon} tone={kk.tone} sub={kk.sub} testId={kk.id} />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <CollapsibleSection id="spend-over-time" title="Spend Over Time" defaultOpen testid="section-spend-time"
          actions={<Select value={granularity} onValueChange={setGranularity}>
            <SelectTrigger data-testid="granularity-select" className="h-7 w-28 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="quarterly">Quarterly</SelectItem>
              <SelectItem value="yearly">Yearly</SelectItem>
            </SelectContent>
          </Select>}>
          <div className="h-72 p-4" data-testid="spend-time-chart">
            {data.spend_over_time.length === 0 ? <Empty>No spend data available for the selected period.</Empty> : (
              <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                <BarChart data={data.spend_over_time}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 100000 ? `₹${(v / 100000).toFixed(1)}L` : `₹${v}`)} />
                  <Tooltip formatter={(v, name) => [fmtMoney(v), name === "base" ? "Base Value" : "GST"]}
                    labelFormatter={(l) => { const b = data.spend_over_time.find((x) => x.label === l); return `${l} · ${b?.count || 0} POs · Final ${fmtMoney(b?.final)}`; }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="base" name="Base Value" stackId="a" fill="#0F172A" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="gst" name="GST" stackId="a" fill="#D97706" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </CollapsibleSection>

        <CollapsibleSection id="spend-by-category" title="Spend by Category" defaultOpen testid="section-spend-category">
          <div className="grid grid-cols-1 gap-2 p-4 md:grid-cols-2">
            <div className="h-64" data-testid="category-chart">
              {data.by_category.length === 0 ? <Empty>No spend data available for the selected period.</Empty> : (
                <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                  <PieChart>
                    <Pie data={data.by_category} dataKey="spend" nameKey="name" innerRadius={50} outerRadius={85}
                      onClick={(d) => drill({ category: d.name })} className="cursor-pointer" label={({ percent }) => `${(percent * 100).toFixed(0)}%`}>
                      {data.by_category.map((c, i) => <Cell key={c.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => fmtMoney(v)} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="max-h-64 overflow-y-auto">
              <table className="w-full text-sm" data-testid="category-table">
                <thead><tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-1.5 pr-2 font-medium">Category</th><th className="py-1.5 pr-2 font-medium text-right">Spend</th>
                  <th className="py-1.5 pr-2 font-medium text-right">%</th><th className="py-1.5 font-medium text-right">POs</th></tr></thead>
                <tbody>
                  {data.by_category.map((c) => (
                    <tr key={c.name} data-testid={`category-row-${c.name.replace(/\W+/g, "-").toLowerCase()}`} onClick={() => drill({ category: c.name })}
                      className="cursor-pointer border-b last:border-0 hover:bg-slate-50">
                      <td className="py-2 pr-2 text-xs font-medium">{c.name}</td>
                      <td className="py-2 pr-2 text-right font-mono text-xs">{fmtMoney(c.spend)}</td>
                      <td className="py-2 pr-2 text-right font-mono text-xs">{c.pct}%</td>
                      <td className="py-2 text-right font-mono text-xs">{c.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </CollapsibleSection>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <CollapsibleSection id="spend-by-supplier" title="Top Suppliers by Spend" defaultOpen testid="section-spend-supplier">
            <div className="max-h-96 overflow-auto">
              <table className="w-full text-sm" data-testid="supplier-spend-table">
                <thead className="sticky top-0 bg-white">
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-2 font-medium">#</th><th className="px-4 py-2 font-medium">Supplier</th>
                    <th className="px-4 py-2 font-medium text-right">POs</th><th className="px-4 py-2 font-medium text-right">Base Value</th>
                    <th className="px-4 py-2 font-medium text-right">GST</th><th className="px-4 py-2 font-medium text-right">Final Value</th>
                    <th className="px-4 py-2 font-medium text-right">%</th><th className="px-4 py-2 font-medium text-right">Savings</th>
                    <th className="px-4 py-2 font-medium text-right">Avg PO</th><th className="px-4 py-2 font-medium text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_suppliers.map((s) => (
                    <tr key={s.name} data-testid={`supplier-spend-${s.name.replace(/\W+/g, "-").toLowerCase()}`} className="border-b last:border-0 hover:bg-slate-50">
                      <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{s.rank}</td>
                      <td className="px-4 py-2.5 font-medium">{s.name}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-xs">{s.count}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-xs">{fmtMoney(s.base)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-xs">{fmtMoney(s.gst)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-xs font-semibold">{fmtMoney(s.spend)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-xs">{s.pct}%</td>
                      <td className="px-4 py-2.5 text-right font-mono text-xs text-[#166534]">{fmtMoney(s.savings)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-xs">{fmtMoney(s.avg_po)}</td>
                      <td className="px-4 py-2.5 text-right">
                        <Button variant="ghost" size="sm" data-testid={`view-supplier-${s.rank}`} onClick={() => navigate(`/buyer/suppliers`)}>View Supplier</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.top_suppliers.length === 0 && <Empty>No spend data available for the selected period.</Empty>}
            </div>
          </CollapsibleSection>
        </div>
        <CollapsibleSection id="supplier-concentration" title="Supplier Concentration" defaultOpen testid="section-concentration">
          <div className="space-y-3 p-4" data-testid="concentration-bars">
            {[["Top 5 Suppliers", data.concentration.top5], ["Top 10 Suppliers", data.concentration.top10],
              ["Top 20 Suppliers", data.concentration.top20], ["Other Suppliers", data.concentration.other]].map(([label, v]) => (
              <div key={label}>
                <div className="flex justify-between text-xs"><span className="text-muted-foreground">{label}</span><span className="font-mono font-semibold">{v}%</span></div>
                <div className="mt-1 h-2 rounded-full bg-slate-100"><div className="h-2 rounded-full bg-slate-900" style={{ width: `${Math.min(v, 100)}%` }} /></div>
              </div>
            ))}
            <p className="text-xs text-muted-foreground">High concentration indicates supplier dependency risk.</p>
          </div>
        </CollapsibleSection>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <CollapsibleSection id="spend-by-department" title="Spend by Department" defaultOpen={false} testid="section-spend-dept">
          <div className="h-64 p-4" data-testid="department-chart">
            {data.by_department.length === 0 ? <Empty>No spend data available for the selected period.</Empty> : (
              <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                <BarChart data={data.by_department.slice(0, 10)} layout="vertical" margin={{ left: 8, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E2E8F0" />
                  <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 100000 ? `₹${(v / 100000).toFixed(1)}L` : `₹${v}`)} />
                  <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => fmtMoney(v)} />
                  <Bar dataKey="spend" fill="#2563EB" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </CollapsibleSection>
        <CollapsibleSection id="spend-by-location" title="Spend by Location" defaultOpen testid="section-spend-location">
          <div className="max-h-64 overflow-y-auto">
            <table className="w-full text-sm" data-testid="location-table">
              <thead><tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-2 font-medium">Location</th><th className="px-4 py-2 font-medium text-right">Spend</th>
                <th className="px-4 py-2 font-medium text-right">POs</th><th className="px-4 py-2 font-medium text-right">Suppliers</th>
                <th className="px-4 py-2 font-medium text-right">Avg PO</th></tr></thead>
              <tbody>
                {data.by_location.map((l) => (
                  <tr key={l.name} data-testid={`location-row-${l.name.replace(/\W+/g, "-").toLowerCase()}`} onClick={() => drill({ location: l.name })}
                    className="cursor-pointer border-b last:border-0 hover:bg-slate-50">
                    <td className="px-4 py-2 font-medium">{l.name}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs">{fmtMoney(l.spend)}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs">{l.count}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs">{l.supplier_count}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs">{fmtMoney(l.avg_po)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.by_location.length === 0 && <Empty>No spend data available for the selected period.</Empty>}
          </div>
        </CollapsibleSection>
      </div>

      <CollapsibleSection id="spend-by-material" title="Top Materials by Spend" defaultOpen={false} testid="section-spend-material">
        <div className="max-h-96 overflow-auto">
          <table className="w-full text-sm" data-testid="material-spend-table">
            <thead className="sticky top-0 bg-white">
              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-4 py-2 font-medium">#</th><th className="px-4 py-2 font-medium">Material Code</th>
                <th className="px-4 py-2 font-medium">Description</th><th className="px-4 py-2 font-medium">Category</th>
                <th className="px-4 py-2 font-medium text-right">Qty</th><th className="px-4 py-2 font-medium">UOM</th>
                <th className="px-4 py-2 font-medium text-right">Suppliers</th><th className="px-4 py-2 font-medium text-right">POs</th>
                <th className="px-4 py-2 font-medium text-right">Total Spend</th><th className="px-4 py-2 font-medium text-right">Avg Unit Price</th>
              </tr>
            </thead>
            <tbody>
              {data.top_materials.map((m, i) => (
                <tr key={m.code + i} data-testid={`material-row-${m.code}`} onClick={() => drill({ material: m.code })}
                  className="cursor-pointer border-b last:border-0 hover:bg-slate-50">
                  <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{i + 1}</td>
                  <td className="px-4 py-2 font-mono text-xs font-semibold">{m.code}</td>
                  <td className="max-w-48 truncate px-4 py-2 text-xs">{m.description}</td>
                  <td className="px-4 py-2 text-xs">{m.category}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs">{m.qty}</td>
                  <td className="px-4 py-2 font-mono text-xs">{m.uom}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs">{m.supplier_count}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs">{m.po_count}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs font-semibold">{fmtMoney(m.spend)}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs">{fmtMoney(m.avg_unit_price)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.top_materials.length === 0 && <Empty>No material spend available for the selected period.</Empty>}
        </div>
      </CollapsibleSection>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <CollapsibleSection id="savings-analytics" title="Savings Analytics" defaultOpen={false} testid="section-savings">
          <div className="space-y-3 p-4" data-testid="savings-cards">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-md border p-3"><p className="text-xs text-muted-foreground">Total Savings</p><p className="mt-1 font-mono text-lg font-bold text-[#166534]" data-testid="savings-total">{fmtMoney(data.savings.total)}</p></div>
              <div className="rounded-md border p-3"><p className="text-xs text-muted-foreground">Auction Savings</p><p className="mt-1 font-mono text-lg font-bold">{fmtMoney(data.savings.auction_savings)}</p></div>
            </div>
            <div className="rounded-md border p-3"><p className="text-xs text-muted-foreground">Competitive Quote Spread</p><p className="mt-1 font-mono text-sm font-semibold">{fmtMoney(data.savings.competitive_savings)}</p></div>
            <p className="text-[11px] text-muted-foreground">{data.savings.note}</p>
          </div>
        </CollapsibleSection>
        <CollapsibleSection id="auction-analytics" title="Auction Spend" defaultOpen={false} testid="section-auction-analytics">
          <div className="grid grid-cols-2 gap-3 p-4" data-testid="auction-analytics-cards">
            {[["Auction Spend", fmtMoney(data.auction.spend)], ["Auction Savings", fmtMoney(data.auction.savings)],
              ["Auctions", data.auction.count], ["Avg Savings %", `${data.auction.savings_pct}%`],
              ["Avg Participants", data.auction.avg_participants], ["Avg Bids", data.auction.avg_bids]].map(([l, v]) => (
              <div key={l} className="rounded-md border p-3"><p className="text-xs text-muted-foreground">{l}</p><p className="mt-1 font-mono text-sm font-bold">{v}</p></div>
            ))}
          </div>
          {data.auction.count === 0 && <Empty>No auction spend available for this period.</Empty>}
        </CollapsibleSection>
        <CollapsibleSection id="procurement-method" title="Spend by Procurement Method" defaultOpen={false} testid="section-method">
          <div className="space-y-2 p-4" data-testid="method-list">
            {data.by_method.map((m) => (
              <div key={m.name} data-testid={`method-${m.name.replace(/\W+/g, "-").toLowerCase()}`} className="flex items-center justify-between rounded-md border px-3 py-2">
                <span className="text-sm font-medium">{m.name}</span>
                <span className="font-mono text-xs">{fmtMoney(m.spend)} · {m.count} POs · saves {fmtMoney(m.savings)}</span>
              </div>
            ))}
            {data.by_method.length === 0 && <Empty>No spend data available for the selected period.</Empty>}
          </div>
        </CollapsibleSection>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <CollapsibleSection id="price-variance" title="Unit Price Trends & Purchase Price Variance" defaultOpen={false} testid="section-price-trends">
          <div className="max-h-80 overflow-auto">
            <table className="w-full text-sm" data-testid="price-trend-table">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2 font-medium">Material</th><th className="px-4 py-2 font-medium text-right">First</th>
                  <th className="px-4 py-2 font-medium text-right">Current</th><th className="px-4 py-2 font-medium text-right">Avg</th>
                  <th className="px-4 py-2 font-medium text-right">Low</th><th className="px-4 py-2 font-medium text-right">High</th>
                  <th className="px-4 py-2 font-medium text-right">Change</th><th className="px-4 py-2 font-medium">Trend</th>
                </tr>
              </thead>
              <tbody>
                {data.price_trends.map((t) => (
                  <tr key={t.code} data-testid={`price-trend-${t.code}`} className="border-b last:border-0">
                    <td className="px-4 py-2 font-mono text-xs font-semibold">{t.code}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs">{fmtMoney(t.first)}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs">{fmtMoney(t.current)}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs">{fmtMoney(t.avg)}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs">{fmtMoney(t.lowest)}</td>
                    <td className="px-4 py-2 text-right font-mono text-xs">{fmtMoney(t.highest)}</td>
                    <td className={`px-4 py-2 text-right font-mono text-xs font-semibold ${t.change_pct > 2 ? "text-[#DC2626]" : t.change_pct < -2 ? "text-[#166534]" : ""}`}>
                      {t.change_pct > 0 ? "+" : ""}{t.change_pct}%
                    </td>
                    <td className="px-4 py-2"><span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase ${t.direction === "increase" ? "border-[#FECACA] bg-[#FEE2E2] text-[#991B1B]" : t.direction === "decrease" ? "border-[#BBF7D0] bg-[#DCFCE7] text-[#166534]" : "border-slate-200 bg-slate-100 text-slate-600"}`}>{t.direction}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.price_trends.length === 0 && <Empty>No price history available for the selected period.</Empty>}
          </div>
        </CollapsibleSection>
        <div className="space-y-4">
          <CollapsibleSection id="maverick-spend" title="Maverick Spend" defaultOpen={false} testid="section-maverick">
            <div className="space-y-3 p-4" data-testid="maverick-cards">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-md border p-3"><p className="text-xs text-muted-foreground">Maverick Spend</p><p className="mt-1 font-mono text-sm font-bold" data-testid="maverick-value">{fmtMoney(data.maverick.spend)}</p></div>
                <div className="rounded-md border p-3"><p className="text-xs text-muted-foreground">% of Total</p><p className="mt-1 font-mono text-sm font-bold">{data.maverick.pct}%</p></div>
                <div className="rounded-md border p-3"><p className="text-xs text-muted-foreground">POs</p><p className="mt-1 font-mono text-sm font-bold">{data.maverick.count}</p></div>
              </div>
              <p className="text-[11px] text-muted-foreground">Maverick spend = POs without a linked, approved procurement event.</p>
            </div>
          </CollapsibleSection>
          <CollapsibleSection id="contracted-budget" title="Contract & Budget Spend" defaultOpen={false} testid="section-contract-budget">
            <Empty>Contract spend analytics will be available once contracts are linked to procurement events.</Empty>
            <Empty>Budget analytics will be available once budgets are configured.</Empty>
          </CollapsibleSection>
          <CollapsibleSection id="po-analytics" title="PO Spend & Status" defaultOpen={false} testid="section-po-analytics">
            <div className="grid grid-cols-3 gap-3 p-4" data-testid="po-stats-cards">
              {[["Total POs", data.po_stats.total], ["Open", data.po_stats.open], ["Completed", data.po_stats.completed],
                ["Cancelled", data.po_stats.cancelled], ["Largest PO", fmtMoney(data.po_stats.largest)], ["Smallest PO", fmtMoney(data.po_stats.smallest)]].map(([l, v]) => (
                <div key={l} className="rounded-md border p-3"><p className="text-xs text-muted-foreground">{l}</p><p className="mt-1 font-mono text-sm font-bold">{v}</p></div>
              ))}
            </div>
            <div className="border-t px-4 pb-4">
              <p className="mb-2 mt-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Status Distribution</p>
              {data.status_distribution.map((s) => (
                <div key={s.name} className="flex items-center justify-between border-b py-1.5 text-xs last:border-0">
                  <span className="capitalize">{s.name.replace(/_/g, " ")}</span>
                  <span className="font-mono">{s.count} POs · {fmtMoney(s.spend)}</span>
                </div>
              ))}
            </div>
          </CollapsibleSection>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <CollapsibleSection id="spend-at-risk" title="Spend at Risk" defaultOpen={false} testid="section-at-risk"
          badge={data.at_risk.po_count > 0 && <span className="rounded-full border border-[#FECACA] bg-[#FEE2E2] px-2 py-0.5 text-[10px] font-semibold text-[#991B1B]">{data.at_risk.po_count} at risk</span>}>
          <div className="p-4">
            <div className="mb-3 grid grid-cols-3 gap-3">
              <div className="rounded-md border p-3"><p className="text-xs text-muted-foreground">At-Risk Spend</p><p className="mt-1 font-mono text-sm font-bold text-[#DC2626]" data-testid="at-risk-value">{fmtMoney(data.at_risk.spend)}</p></div>
              <div className="rounded-md border p-3"><p className="text-xs text-muted-foreground">POs</p><p className="mt-1 font-mono text-sm font-bold">{data.at_risk.po_count}</p></div>
              <div className="rounded-md border p-3"><p className="text-xs text-muted-foreground">Suppliers</p><p className="mt-1 font-mono text-sm font-bold">{data.at_risk.supplier_count}</p></div>
            </div>
            {data.at_risk.items.map((i) => (
              <div key={i.po_number} className="flex items-center justify-between border-b py-1.5 text-xs last:border-0" data-testid={`at-risk-${i.po_number}`}>
                <span className="font-mono font-semibold">{i.po_number} <span className="font-normal text-muted-foreground">· {i.supplier}</span></span>
                <span className="font-mono">{fmtMoney(i.value)} · <span className="text-[#DC2626]">{i.reason}</span></span>
              </div>
            ))}
            {data.at_risk.items.length === 0 && <Empty>No spend at risk in the selected period.</Empty>}
          </div>
        </CollapsibleSection>
        <CollapsibleSection id="supplier-dependency" title="Supplier Dependency Analysis" defaultOpen={false} testid="section-dependency">
          <div className="max-h-80 overflow-auto p-4">
            {data.dependency.map((d) => (
              <div key={d.material} data-testid={`dependency-${d.material}`} className="mb-2 flex items-center justify-between rounded-md border px-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="font-mono text-xs font-semibold">{d.material}</p>
                  <p className="truncate text-xs text-muted-foreground">{d.description} · {d.supplier}</p>
                </div>
                <span className="mr-2 font-mono text-xs">{d.share}%</span>
                <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${RISK_STYLE[d.risk]}`}>{d.risk}</span>
              </div>
            ))}
            {data.dependency.length === 0 && <Empty>No material dependency data available.</Empty>}
          </div>
        </CollapsibleSection>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <CollapsibleSection id="cycle-competition" title="Procurement Cycle Time & Competition" defaultOpen={false} testid="section-cycle">
          <div className="grid grid-cols-2 gap-3 p-4" data-testid="cycle-cards">
            {[["Supplier Response", data.cycle_time.supplier_response], ["Technical Review", data.cycle_time.technical_review],
              ["Approval", data.cycle_time.approval], ["Enquiry → Award", data.cycle_time.enquiry_to_award]].map(([l, s]) => (
              <div key={l} className="rounded-md border p-3">
                <p className="text-xs text-muted-foreground">{l}</p>
                <p className="mt-1 font-mono text-sm font-bold">{s.avg != null ? `${s.avg}d avg` : "—"}</p>
                {s.median != null && <p className="text-[10px] text-muted-foreground">median {s.median}d · longest {s.max}d</p>}
              </div>
            ))}
            {[["Avg Sellers Invited", data.competition.avg_invited], ["Avg Responses", data.competition.avg_responses],
              ["Response Rate", `${data.competition.response_rate}%`], ["Events", data.competition.events]].map(([l, v]) => (
              <div key={l} className="rounded-md border p-3"><p className="text-xs text-muted-foreground">{l}</p><p className="mt-1 font-mono text-sm font-bold">{v}</p></div>
            ))}
          </div>
        </CollapsibleSection>
        <CollapsibleSection id="spend-insights" title="Insights & Savings Opportunities" defaultOpen testid="section-insights">
          <div className="space-y-4 p-4">
            <div>
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><Lightbulb className="h-3.5 w-3.5" /> Insights</p>
              <div className="space-y-1.5" data-testid="insights-list">
                {data.insights.map((ins, i) => (
                  <p key={ins} className="rounded-md border border-blue-100 bg-blue-50/50 px-3 py-2 text-sm" data-testid={`insight-${i}`}>{ins}</p>
                ))}
                {data.insights.length === 0 && <Empty>No insights available — insufficient data for the selected period.</Empty>}
              </div>
            </div>
            <div>
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"><Target className="h-3.5 w-3.5" /> Savings Opportunities</p>
              <div className="space-y-1.5" data-testid="opportunities-list">
                {data.opportunities.map((o, i) => (
                  <div key={o.title} className="rounded-md border border-[#FDE68A] bg-[#FEF3C7]/40 px-3 py-2" data-testid={`opportunity-${i}`}>
                    <p className="text-sm font-medium">{o.title}</p>
                    <p className="text-xs text-muted-foreground">{o.detail}</p>
                    <p className="mt-1 text-xs font-medium text-[#92400E]">Suggested action: {o.action}</p>
                  </div>
                ))}
                {data.opportunities.length === 0 && <Empty>No savings opportunities identified for the selected period.</Empty>}
              </div>
            </div>
          </div>
        </CollapsibleSection>
      </div>

      <CollapsibleSection id="spend-top10" title="Top 10" defaultOpen={false} testid="section-top10">
        <div className="grid grid-cols-1 gap-4 p-4 md:grid-cols-2 lg:grid-cols-4">
          {[["Top Suppliers", data.top10.suppliers.map((s) => [s.name, fmtMoney(s.spend)])],
            ["Top Categories", data.top10.categories.map((c) => [c.name, fmtMoney(c.spend)])],
            ["Top Materials", data.top10.materials.map((m) => [m.code, fmtMoney(m.spend)])],
            ["Top POs by Value", data.top10.pos.map((p) => [p.po_number, fmtMoney(p.value)])]].map(([title, rows]) => (
            <div key={title}>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
              {rows.map(([l, v], i) => (
                <div key={l} className="flex items-center justify-between border-b py-1 text-xs last:border-0">
                  <span className="min-w-0 flex-1 truncate pr-2">{i + 1}. {l}</span>
                  <span className="font-mono font-semibold">{v}</span>
                </div>
              ))}
              {rows.length === 0 && <p className="text-xs text-muted-foreground">No data.</p>}
            </div>
          ))}
        </div>
      </CollapsibleSection>

      <CollapsibleSection id="detailed-spend" title="Detailed Spend" defaultOpen={false} testid="section-detailed-spend"
        actions={<div className="relative">
          <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-muted-foreground" />
          <Input data-testid="detail-search" className="h-8 w-56 pl-7 text-xs" placeholder="Search PO, supplier, material…"
            value={tableQ.search} onChange={(e) => setTableQ({ ...tableQ, search: e.target.value, page: 1 })} />
        </div>}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="detailed-spend-table">
            <thead className="sticky top-0 bg-white">
              <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                {[["date", "Date"], ["po_number", "PO Number"], ["enquiry_number", "Event"], ["department", "Department"],
                  ["category", "Category"], ["supplier", "Supplier"], ["base", "Base Value"], ["gst", "GST"], ["value", "Final Value"], ["status", "Status"]].map(([key, label]) => (
                  <th key={key} className="px-3 py-2 font-medium">
                    <button className="flex items-center gap-1 uppercase" data-testid={`sort-${key}`}
                      onClick={() => setTableQ({ ...tableQ, sort: key, order: tableQ.sort === key && tableQ.order === "desc" ? "asc" : "desc" })}>
                      {label} <ArrowUpDown className="h-3 w-3" />
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((r) => (
                <tr key={r.po_id} data-testid={`spend-row-${r.po_number}`} onClick={() => navigate(`/buyer/pos/${r.po_id}`)}
                  className="cursor-pointer border-b last:border-0 hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono text-xs">{r.date.slice(0, 10)}</td>
                  <td className="px-3 py-2 font-mono text-xs font-semibold">{r.po_number}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.enquiry_number || "—"}</td>
                  <td className="px-3 py-2 text-xs">{r.department}</td>
                  <td className="px-3 py-2 text-xs">{r.category}</td>
                  <td className="max-w-40 truncate px-3 py-2 text-xs">{r.supplier}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{fmtMoney(r.base)}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{fmtMoney(r.gst)}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs font-semibold">{fmtMoney(r.value)}</td>
                  <td className="px-3 py-2 text-xs capitalize">{r.status.replace(/_/g, " ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {table.rows.length === 0 && <Empty>No spend data available for the selected period.</Empty>}
        </div>
        <div className="flex items-center justify-between border-t px-4 py-2.5">
          <p className="text-xs text-muted-foreground" data-testid="detail-total">{table.total} PO(s) · page {table.page} of {table.pages}</p>
          <div className="flex gap-1">
            <Button variant="outline" size="icon" className="h-7 w-7" data-testid="detail-prev" disabled={table.page <= 1}
              onClick={() => setTableQ({ ...tableQ, page: tableQ.page - 1 })}><ChevronLeft className="h-3.5 w-3.5" /></Button>
            <Button variant="outline" size="icon" className="h-7 w-7" data-testid="detail-next" disabled={table.page >= table.pages}
              onClick={() => setTableQ({ ...tableQ, page: tableQ.page + 1 })}><ChevronRight className="h-3.5 w-3.5" /></Button>
          </div>
        </div>
      </CollapsibleSection>
    </div>
  );
}
